import React, { forwardRef, ReactElement, useCallback } from 'react'
import { MenuItem, MenuList, MenuListProps } from '@chakra-ui/react'

export interface CustomMenuListProps {
  items: {
    id: string
    label: string
    action?: () => void
    icon?: ReactElement
    disabled?: boolean
  }[]
  onChange?: (item: any) => void
  selected?: {
    id: string
    label: string
    action?: () => void
    icon?: ReactElement
    disabled?: boolean
  }
  children?: React.ReactNode
}

const CustomMenuList = forwardRef(
  (props: CustomMenuListProps & MenuListProps, ref) => {
    const { items, onChange, selected, children, ...rest } = props
    const handleItemClick = useCallback(
      (e) => {
        e.preventDefault()
        e.stopPropagation()
    
        const itemElement = e.currentTarget
        const itemId = itemElement.dataset.id
        
        if (!itemId) {
          console.error('Missing data-id in clicked element:', itemElement)
          return
        }

        const item = items.find(i => i.id === itemId)

        if (!item) {
          console.error('Item not found for id:', itemId, 'Available items:', items)
          return
        }
    
        if (item.action) {
          item.action()
          return
        }
    
        if (onChange) {
          onChange(item)
        }
      },
      [items, onChange]
    )

    return (
      <MenuList
        border="none"
        borderRadius="8px"
        boxShadow="0 4px 14px 0 rgba(0,0,0,0.1)"
        p="12px"
        {...rest}
        ref={ref}
        data-testid="custom-menu-list"
        zIndex={3}>
        {items.map((item, index) => (
          <MenuItem
            fontSize="14px"
            fontWeight="bold"
            lineHeight="3em"
            color="brand.textDarkBlue"
            borderRadius="5px"
            _hover={{ backgroundColor: 'brand.lightGray' }}
            _active={{ backgroundColor: 'brand.lightGray' }}
            backgroundColor={
              item.id === selected?.id ? 'brand.lightestGray' : 'transparent'
            }
            disabled={item.disabled}
            p="0 12px"
            data-testid="dropdown-item"
            key={index}
            onClick={handleItemClick}
            data-id={item.id}
            opacity={item.disabled ? 0.5 : 1}
            pointerEvents={item.disabled ? 'none' : 'auto'}
            icon={item.icon}>
            {item.label}
          </MenuItem>
        ))}
        {children}
      </MenuList>
    )
  },
)

export default CustomMenuList
